import { TranslateService } from '@ngx-translate/core';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';
import { WindowLayoutService } from '@dg/shared-services';
import { SubscriberBaseDirective } from '../subscriber-base/subscriber-base.directive';
import { SkeletonComponent } from '@app/skeleton/skeleton.component';
import { CommonModule } from '@angular/common';
import { StickyDirective } from '@app/shared/directives/sticky.directive';

/**
 * This component provides a sticky wrapper that allows the user to collapse and expand
 * the filters provided to it for a11y purposes.
 *
 * @example
 * <dgx-sticky-filter-bar [showSkeleton]="isLoading" [filterCount]="filters.length">
 *   // Your filter components go here
 * </dgx-sticky-filter-bar>
 */
@Component({
    selector: 'dgx-sticky-filter-bar',
    templateUrl: './sticky-filter-bar.component.html',
    styleUrls: ['./sticky-filter-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, SkeletonComponent, StickyDirective]
})
export class StickyFilterBarComponent
  extends SubscriberBaseDirective
  implements OnInit
{
  /** Display the loading skeleton */
  @Input() public showSkeleton: boolean;
  /** Columns to display in the loading skeleton */
  @Input() public filterCount: number = 4;

  public collapsedForMobile$ = new BehaviorSubject<boolean>(false);
  public isMobile$: Observable<boolean>;
  public showFilters$: Observable<boolean>;
  public toggleButtonLabel$: Observable<string>;

  public i18n: Record<string, string> = this.translateService.instant([
    'Core_ShowFilters',
    'Core_HideFilters',
    'Core_SearchFilterA11yLabel',
  ]);

  constructor(
    private translateService: TranslateService,
    private windowLayoutService: WindowLayoutService
  ) {
    super();
  }

  public ngOnInit() {
    const {
      dgBreakpoints: { BabyBear, TeenieBear },
    } = this.windowLayoutService;

    this.isMobile$ = this.windowLayoutService
      .registerMediaListener([BabyBear, TeenieBear], false)
      .pipe(
        this.takeUntilDestroyed(),
        map(({ matches }) => matches)
      );

    this.showFilters$ = combineLatest([
      this.collapsedForMobile$,
      this.isMobile$,
    ]).pipe(map(this.getShowFilters.bind(this)));

    this.toggleButtonLabel$ = this.collapsedForMobile$.pipe(
      map(this.getButtonLabel.bind(this))
    );
  }

  public toggleFilters() {
    const collapsed = this.collapsedForMobile$.value;

    this.collapsedForMobile$.next(!collapsed);
  }

  // If the user collapses the filters in a small window, then resizes
  // the window to a larger size, we should display the filters again
  private getShowFilters([collapsed, isMobile]) {
    return !collapsed || !isMobile;
  }

  private getButtonLabel(collapsed: boolean) {
    return collapsed ? this.i18n.Core_ShowFilters : this.i18n.Core_HideFilters;
  }
}
